'use client';

import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';

interface AreaScores {
  administrativeOrganization: number;
  financialManagement: number;
  humanResources: number;
  publicProcurement: number;
}

interface ScoreBreakdownProps {
  areaScores: AreaScores;
  totalScore: number;
}

const AREA_MAX = 4;

const AREA_LABELS: Record<keyof AreaScores, { label: string; shortLabel: string }> = {
  administrativeOrganization: { label: 'Organização Administrativa', shortLabel: 'Org. Admin.' },
  financialManagement: { label: 'Gestão Financeira', shortLabel: 'Gest. Fin.' },
  humanResources: { label: 'Recursos Humanos', shortLabel: 'Rec. Hum.' },
  publicProcurement: { label: 'Contratação Pública', shortLabel: 'Cont. Púb.' },
};

function getScoreColor(score: number): string {
  if (score <= 1) return '#2E7D32';
  if (score === 2) return '#F57C00';
  if (score === 3) return '#E65100';
  return '#C62828';
}

function getScoreLabel(score: number): string {
  if (score <= 1) return 'Adequado';
  if (score === 2) return 'Atenção';
  if (score === 3) return 'Crítico';
  return 'Urgente';
}

interface CustomTooltipProps {
  active?: boolean;
  payload?: Array<{ value: number; payload: { fullLabel: string } }>;
}

function CustomTooltip({ active, payload }: CustomTooltipProps) {
  if (active && payload && payload.length) {
    const score = payload[0].value;
    return (
      <div
        className="bg-white border border-gray-100 p-3"
        style={{ borderRadius: '5px', boxShadow: '0 3px 8px rgba(0,0,0,0.10)' }}
      >
        <p className="text-gray-400 text-xs mb-1" style={{ fontFamily: 'DM Sans, sans-serif' }}>
          {payload[0].payload.fullLabel}
        </p>
        <p className="font-semibold text-sm text-gray-900" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
          {score}/{AREA_MAX} pontos
        </p>
        <p className="text-xs mt-1 font-semibold" style={{ color: getScoreColor(score), fontFamily: 'DM Sans, sans-serif' }}>
          {getScoreLabel(score)}
        </p>
      </div>
    );
  }
  return null;
}

export default function ScoreBreakdown({ areaScores, totalScore }: ScoreBreakdownProps) {
  const chartData = (Object.keys(areaScores) as Array<keyof AreaScores>).map((key) => ({
    name: AREA_LABELS[key].shortLabel,
    fullLabel: AREA_LABELS[key].label,
    score: areaScores[key],
    fill: getScoreColor(areaScores[key]),
  }));

  return (
    <div
      className="bg-white border border-gray-100 p-7"
      style={{ borderRadius: '5px', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}
    >
      <div className="flex items-center gap-2 mb-6">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#009688" strokeWidth="2" aria-hidden="true">
          <line x1="18" y1="20" x2="18" y2="10"/>
          <line x1="12" y1="20" x2="12" y2="4"/>
          <line x1="6" y1="20" x2="6" y2="14"/>
        </svg>
        <h3
          className="text-gray-900 font-semibold"
          style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '1.15rem', fontWeight: 700 }}
        >
          Análise por Área
        </h3>
      </div>

      {/* Chart */}
      <div className="w-full h-44 mb-6" aria-label="Gráfico de barras com pontuação por área de avaliação">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartData} margin={{ top: 5, right: 10, left: -20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
            <XAxis
              dataKey="name"
              tick={{ fontSize: 11, fontFamily: 'DM Sans, sans-serif', fill: '#888' }}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              domain={[0, AREA_MAX]}
              ticks={[0, 1, 2, 3, 4]}
              tick={{ fontSize: 11, fontFamily: 'JetBrains Mono, monospace', fill: '#888' }}
              axisLine={false}
              tickLine={false}
            />
            <Tooltip content={<CustomTooltip />} />
            <Bar dataKey="score" radius={[3, 3, 0, 0]} maxBarSize={56}>
              {chartData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.fill} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Area cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {(Object.keys(areaScores) as Array<keyof AreaScores>).map((key) => {
          const score = areaScores[key];
          const config = AREA_LABELS[key];
          const color = getScoreColor(score);
          const label = getScoreLabel(score);
          const percent = Math.round((score / AREA_MAX) * 100);

          return (
            <div
              key={key}
              className="border border-gray-100 p-4"
              style={{ borderRadius: '5px', backgroundColor: '#F9F9F9' }}
            >
              <div className="flex items-center justify-between mb-2">
                <span
                  className="text-xs font-semibold text-gray-700"
                  style={{ fontFamily: 'DM Sans, sans-serif' }}
                >
                  {config.label}
                </span>
                <span
                  className="text-xs font-semibold px-2 py-0.5"
                  style={{
                    fontFamily: 'DM Sans, sans-serif',
                    borderRadius: '5px',
                    backgroundColor: `${color}18`,
                    color,
                  }}
                >
                  {label}
                </span>
              </div>
              <div className="flex items-center gap-3">
                <div
                  className="flex-1 h-1.5 bg-white overflow-hidden"
                  style={{ borderRadius: '0', border: '1px solid #E0E0E0' }}
                >
                  <div
                    className="h-full transition-all duration-500"
                    style={{ width: `${percent}%`, backgroundColor: color, borderRadius: '0' }}
                  />
                </div>
                <span
                  className="text-xs font-semibold text-gray-700 w-10 text-right"
                  style={{ fontFamily: 'JetBrains Mono, monospace' }}
                >
                  {score}/{AREA_MAX}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Legend */}
      <div
        className="flex flex-wrap gap-5 mt-5 pt-5"
        style={{ borderTop: '1px solid #F0F0F0' }}
      >
        {[
          { label: 'Adequado', color: '#2E7D32' },
          { label: 'Atenção', color: '#F57C00' },
          { label: 'Crítico', color: '#E65100' },
          { label: 'Urgente', color: '#C62828' },
        ].map((item) => (
          <div key={item.label} className="flex items-center gap-2">
            <div className="w-2.5 h-2.5" style={{ backgroundColor: item.color, borderRadius: '2px' }} />
            <span className="text-gray-400 text-xs" style={{ fontFamily: 'DM Sans, sans-serif' }}>{item.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}